import { OilIcon, ServicesIcon, VehicleIcon, DiscoverIcon, PowerIcon, MechanicIcon, GlobeIcon, SettingsIcon, NoteIcon, TowingIcon, AirconIcon } from "../../lib/cccIcons"
import useIsMobile from "../../lib/useIsMobile"
import { useAuth } from "../../contexts/AuthContext"
import { useEffect, useState } from "react"
import { supabase } from "../../lib/supabase"
import toast from "react-hot-toast"

const DEFAULT_CATEGORIES = [
  { name:"Oil Change", icon:"oil", description:"Engine oil and filter replacement" },
  { name:"Brake Repair", icon:"services", description:"Brake pads, rotors and brake fluid" },
  { name:"Tire Service", icon:"vehicle", description:"Rotation, balancing and replacement" },
  { name:"Engine Repair", icon:"⚙️", description:"Diagnostics and engine servicing" },
  { name:"AC Repair", icon:"❄️", description:"Air conditioning service and regas" },
  { name:"Transmission", icon:"🔩", description:"Gearbox and transmission repair" },
  { name:"Detailing", icon:"✨", description:"Interior and exterior detailing" },
  { name:"Maintenance", icon:"📋", description:"General servicing and checkups" },
  { name:"Electrical", icon:"power", description:"Electrical diagnostics and repair" },
  { name:"Body Repair", icon:"🚗", description:"Dents, scratches and panel work" },
]

export default function AdminCategories() {
  const { user, profile } = useAuth()
  const isMobile = useIsMobile()
  const [categories, setCategories] = useState([])
  const [form, setForm] = useState({ name:"", icon:"🔧", description:"" })
  const [editing, setEditing] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  if (!user || profile?.role !== "admin") return null

  useEffect(() => { load() }, [])

  async function load() {
    const { data } = await supabase.from("service_categories").select("*").order("name")
    if (data && data.length > 0) {
      setCategories(data)
    } else {
      setCategories(DEFAULT_CATEGORIES.map((c,i) => ({ ...c, id:i+1, is_active:true, service_count:0 })))
    }
    setLoading(false)
  }

  async function save(e) {
    e.preventDefault()
    setSaving(true)
    try {
      if (editing) {
        const { error } = await supabase.from("service_categories")
          .update({ name:form.name, icon:form.icon, description:form.description })
          .eq("id", editing)
        if (error) throw error
        toast.success("Category updated")
        setEditing(null)
      } else {
        const { error } = await supabase.from("service_categories")
          .insert({ name:form.name, icon:form.icon, description:form.description, is_active:true })
        if (error) throw error
        toast.success("Category added")
      }
      setForm({ name:"", icon:"🔧", description:"" })
      load()
    } catch(err) {
      toast.error(err.message)
    } finally { setSaving(false) }
  }

  async function toggleActive(id, is_active) {
    await supabase.from("service_categories").update({ is_active:!is_active }).eq("id", id)
    toast.success(is_active ? "Category hidden" : "Category visible")
    load()
  }

  async function deleteCategory(id, name) {
    if (!confirm(`Delete category "${name}"? Services using this category will be unaffected.`)) return
    await supabase.from("service_categories").delete().eq("id", id)
    toast.success("Category deleted")
    load()
  }

  function startEdit(c) {
    setEditing(c.id)
    setForm({ name:c.name, icon:c.icon||"🔧", description:c.description||"" })
    window.scrollTo({ top:document.body.scrollHeight, behavior:"smooth" })
  }

  const inp = { width:"100%", background:"#ffffff", border:"1px solid #f0f0f0", borderRadius:8, padding:"10px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", marginBottom:10 }
  const lbl = { fontSize:11, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:4 }

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(3,1fr)", gap:10, marginBottom:"1.5rem" }}>
        {[
          { label:"Total categories", value:categories.length },
          { label:"Active", value:categories.filter(c=>c.is_active!==false).length, color:"#1d9e75" },
          { label:"Hidden", value:categories.filter(c=>c.is_active===false).length },
        ].map(s=>(
          <div key={s.label} style={{ background:"#f8f8f8", borderRadius:10, padding:"1rem", border:"1px solid #eeeeee" }}>
            <div style={{ fontSize:11, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>{s.label}</div>
            <div style={{ fontFamily:"Syne", fontSize:22, fontWeight:800, color:s.color||"#000000" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading&&<div style={{ color:"#888", fontSize:13 }}>Loading...</div>}

      <div style={{ display:"grid", gap:8, marginBottom:"1.5rem" }}>
        {categories.map(c=>(
          <div key={c.id} style={{ background:"#f8f8f8", border:`1px solid ${c.is_active===false?"#e24b4a20":"#eeeeee"}`, borderRadius:10, padding:"1rem", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:10, background:"#f5f5f5", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              {c.icon==="oil"?<OilIcon size={22} color="#e6821e"/>:
               c.icon==="vehicle"?<VehicleIcon size={22} color="#e6821e"/>:
               c.icon==="services"?<ServicesIcon size={22} color="#e6821e"/>:
               c.icon==="discover"?<DiscoverIcon size={22} color="#e6821e"/>:
               c.icon==="power"?<PowerIcon size={22} color="#e6821e"/>:
               c.icon==="settings"?<SettingsIcon size={22} color="#e6821e"/>:
               c.icon==="note"?<NoteIcon size={22} color="#e6821e"/>:
               c.icon==="towing"?<TowingIcon size={22} color="#e6821e"/>:
               c.icon==="aircon"?<AirconIcon size={22} color="#e6821e"/>:
               c.icon==="mechanic"?<MechanicIcon size={22} color="#e6821e"/>:
               c.icon==="globe"?<GlobeIcon size={22} color="#e6821e"/>:
               <ServicesIcon size={22} color="#e6821e"/>}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                <div style={{ fontSize:13, fontWeight:600, color:c.is_active===false?"#555":"#000000" }}>{c.name}</div>
                {c.is_active===false&&<span style={{ fontSize:10, color:"#e24b4a", background:"#fff5f5", padding:"1px 6px", borderRadius:10 }}>Hidden</span>}
              </div>
              {c.description&&<div style={{ fontSize:11, color:"#888" }}>{c.description}</div>}
            </div>
            <div style={{ display:"flex", gap:6, flexShrink:0 }}>
              <button onClick={()=>startEdit(c)}
                style={{ background:"none", border:"1px solid #dddddd", borderRadius:7, color:"#888", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                Edit
              </button>
              {typeof c.is_active !== "undefined" && (
                <button onClick={()=>toggleActive(c.id, c.is_active)}
                  style={{ background:"none", border:`1px solid ${c.is_active===false?"#1d9e7540":"#e24b4a40"}`, borderRadius:7, color:c.is_active===false?"#1d9e75":"#e24b4a", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                  {c.is_active===false?"Show":"Hide"}
                </button>
              )}
              <button onClick={()=>deleteCategory(c.id, c.name)}
                style={{ background:"none", border:"1px solid #e24b4a40", borderRadius:7, color:"#e24b4a", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background:"#f8f8f8", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:"1rem", color:"#000000" }}>
          {editing ? "Edit category" : "Add new category"}
        </div>
        <form onSubmit={save}>
          <div style={{ display:"grid", gridTemplateColumns:"80px 1fr", gap:10 }}>
            <div>
              <label style={lbl}>Icon</label>
              <input style={inp} placeholder="🔧" value={form.icon} onChange={e=>setForm(f=>({...f,icon:e.target.value}))} maxLength={4}/>
            </div>
            <div>
              <label style={lbl}>Category name</label>
              <input style={inp} placeholder="e.g. Suspension Repair" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required/>
            </div>
          </div>
          <label style={lbl}>Description</label>
          <input style={inp} placeholder="Brief description of this category" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
          <div style={{ display:"flex", gap:8 }}>
            <button type="submit" disabled={saving}
              style={{ background:saving?"#e0e0e0":"#8b5cf6", border:"none", borderRadius:8, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"10px 20px", cursor:saving?"not-allowed":"pointer" }}>
              {saving?"Saving...":editing?"Update category":"Add category"}
            </button>
            {editing&&(
              <button type="button" onClick={()=>{ setEditing(null); setForm({ name:"", icon:"🔧", description:"" }) }}
                style={{ background:"none", border:"1px solid #dddddd", borderRadius:8, color:"#888", fontSize:13, padding:"10px 20px", cursor:"pointer" }}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

